import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Users,
  UserCheck,
  Calendar,
  AlertCircle,
  Building2,
  Clock,
  TrendingUp,
  TrendingDown,
  Activity,
  BookOpen,
  UserPlus,
  BarChart3,
  PieChart,
  CalendarDays,
  Mail,
  Phone,
  MapPin,
  Bell,
  Zap,
  CheckCircle,
  Heart,
  Shield,
  Award,
  Target,
  FileText,
  CreditCard,
  GraduationCap,
  Briefcase,
  Eye,
  Plus,
  ArrowRight,
  ChevronRight,
  Star,
  Clock3,
  UserX,
  CheckSquare,
  AlertTriangle,
  Info,
  Play,
  Pause,
  RotateCcw,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { dashboardAPI } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarInitials } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ChartAreaInteractive from "@/components/chart-area-interactive";

const StatCard = ({
  title,
  value,
  icon: Icon,
  color = "blue",
  trend,
  isLoading,
  subtitle,
  onClick,
  className = "",
}) => {
  const getColorClasses = (color) => {
    const colors = {
      blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
      green:
        "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
      yellow:
        "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400",
      red: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400",
      purple:
        "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400",
      indigo:
        "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400",
      teal: "bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400",
      orange:
        "bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400",
    };
    return colors[color] || colors.blue;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-[100px]" />
          <Skeleton className="h-4 w-4" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-[80px] mb-2" />
          <Skeleton className="h-3 w-[120px]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`transition-all duration-300 hover:shadow-lg hover:scale-[1.02] cursor-pointer ${className}`}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardDescription className="text-sm font-medium text-gray-600 dark:text-gray-400">
          {title}
        </CardDescription>
        <div className={`p-3 rounded-xl border ${getColorClasses(color)}`}>
          <Icon className="h-5 w-5" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
          {value}
        </div>
        {subtitle && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            {subtitle}
          </p>
        )}
        {trend !== undefined && (
          <div className="flex items-center text-xs text-muted-foreground">
            {trend > 0 ? (
              <TrendingUp className="h-3 w-3 mr-1 text-green-500" />
            ) : trend < 0 ? (
              <TrendingDown className="h-3 w-3 mr-1 text-red-500" />
            ) : (
              <Activity className="h-3 w-3 mr-1 text-gray-500" />
            )}
            <span
              className={
                trend > 0
                  ? "text-green-500"
                  : trend < 0
                    ? "text-red-500"
                    : "text-gray-500"
              }
            >
              {trend > 0 ? "+" : ""}
              {trend}% vs mois dernier
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ProgressCard = ({
  title,
  value,
  target,
  icon: Icon,
  color = "blue",
  isLoading,
}) => {
  const getColorClasses = (color) => {
    const colors = {
      blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
      green:
        "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
      yellow:
        "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400",
      red: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400",
      purple:
        "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400",
      indigo:
        "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400",
      teal: "bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400",
      orange:
        "bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400",
    };
    return colors[color] || colors.blue;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-[100px]" />
          <Skeleton className="h-4 w-4" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-[80px] mb-2" />
          <Skeleton className="h-3 w-[120px]" />
        </CardContent>
      </Card>
    );
  }

  const percentage = target > 0 ? (value / target) * 100 : 0;
  const isCompleted = percentage >= 100;

  return (
    <Card className="transition-all duration-200 hover:shadow-md">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardDescription className="text-sm font-medium">
          {title}
        </CardDescription>
        <div className={`p-2 rounded-full ${getColorClasses(color)}`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {value} / {target}
          </div>
          <Badge variant={isCompleted ? "success" : "default"}>
            {isCompleted ? "Complété" : `${percentage.toFixed(0)}%`}
          </Badge>
        </div>
        <Progress value={percentage} className="h-2" />
      </CardContent>
    </Card>
  );
};

const ActivityItem = ({
  title,
  description,
  subtitle,
  time,
  type,
  user,
  status,
  isLoading,
}) => {
  const getTypeIcon = (type) => {
    const iconProps = { className: "h-4 w-4" };
    switch (type) {
      case "leave_request":
      case "LEAVE_REQUEST":
        return <Calendar {...iconProps} className="h-4 w-4 text-blue-500" />;
      case "new_employee":
      case "NEW_EMPLOYEE":
        return <UserPlus {...iconProps} className="h-4 w-4 text-green-500" />;
      case "training":
      case "TRAINING":
        return <BookOpen {...iconProps} className="h-4 w-4 text-purple-500" />;
      case "attendance":
      case "ATTENDANCE":
        return <Clock {...iconProps} className="h-4 w-4 text-indigo-500" />;
      case "expense":
        return (
          <CreditCard {...iconProps} className="h-4 w-4 text-orange-500" />
        );
      default:
        return <Bell {...iconProps} className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return null;

    const statusConfig = {
      en_attente: {
        label: "En attente",
        color:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
      },
      approuve: {
        label: "Approuvé",
        color:
          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      },
      refuse: {
        label: "Refusé",
        color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
      },
      soumise: {
        label: "Soumis",
        color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
      },
    };

    const config = statusConfig[status] || statusConfig.en_attente;
    return <Badge className={`text-xs ${config.color}`}>{config.label}</Badge>;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) {
      return `à l'instant`;
    } else if (diffInMinutes < 60) {
      return `il y a ${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""}`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `il y a ${hours} heure${hours > 1 ? "s" : ""}`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `il y a ${days} jour${days > 1 ? "s" : ""}`;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-start space-x-4 p-4">
        <Skeleton className="h-8 w-8 rounded-full" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-[200px]" />
          <Skeleton className="h-3 w-[150px]" />
          <Skeleton className="h-3 w-[100px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start space-x-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-200 group">
      <div className="flex-shrink-0 mt-1 p-2 rounded-lg bg-gray-100 dark:bg-gray-800 group-hover:scale-110 transition-transform">
        {getTypeIcon(type)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {title}
          </p>
          {getStatusBadge(status)}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          {description}
        </p>
        {subtitle && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {subtitle}
          </p>
        )}
        <div className="flex items-center mt-2 text-xs text-gray-400">
          {user && (
            <>
              <span className="font-medium">{user}</span>
              <span className="mx-1">•</span>
            </>
          )}
          <span>{formatTime(time)}</span>
        </div>
      </div>
    </div>
  );
};

const QuickActionButton = ({
  icon: Icon,
  title,
  subtitle,
  onClick,
  variant = "primary",
  badge,
}) => {
  const baseClasses =
    "text-left p-6 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg relative overflow-hidden";
  const variantClasses = {
    primary:
      "bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg",
    secondary:
      "bg-gradient-to-br from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-900 dark:from-gray-800 dark:to-gray-700 dark:hover:from-gray-700 dark:hover:to-gray-600 dark:text-white shadow-md",
    success:
      "bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg",
    warning:
      "bg-gradient-to-br from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white shadow-lg",
    danger:
      "bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg",
    healthcare:
      "bg-gradient-to-br from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white shadow-lg",
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]}`}
      onClick={onClick}
    >
      {badge && (
        <Badge className="absolute top-2 right-2 bg-white/20 text-white border-0">
          {badge}
        </Badge>
      )}
      <div className="flex items-start space-x-4">
        <div className="p-3 rounded-lg bg-white/20 backdrop-blur-sm">
          <Icon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-lg mb-1">{title}</p>
          <p className="text-sm opacity-90">{subtitle}</p>
        </div>
        <ChevronRight className="h-5 w-5 opacity-70" />
      </div>
    </button>
  );
};

export default function DashboardHome() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [dashboardData, setDashboardData] = useState(null);
  const [recentActivities, setRecentActivities] = useState(null);
  const [healthcareMetrics, setHealthcareMetrics] = useState(null);
  const [attendanceChartData, setAttendanceChartData] = useState(null);
  const [departmentStats, setDepartmentStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch all dashboard data in parallel
        const [
          statsResponse,
          activitiesResponse,
          healthcareResponse,
          attendanceResponse,
          departmentResponse,
        ] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getActivities(),
          dashboardAPI.getHealthcareMetrics(),
          dashboardAPI.getAttendanceChart(),
          dashboardAPI.getDepartmentStats(),
        ]);

        if (statsResponse.data.success) {
          setDashboardData(statsResponse.data.data);
        } else {
          throw new Error(
            statsResponse.message ||
            "Erreur lors du chargement des statistiques"
          );
        }

        if (activitiesResponse.data.success) {
          setRecentActivities(activitiesResponse.data.data);
        } else {
          console.warn(
            "Erreur lors du chargement des activités:",
            activitiesResponse.message
          );
          setRecentActivities([]);
        }

        if (healthcareResponse.data.success) {
          setHealthcareMetrics(healthcareResponse.data.data);
        } else {
          console.warn(
            "Erreur lors du chargement des métriques de santé:",
            healthcareResponse.message
          );
          setHealthcareMetrics(null);
        }

        if (attendanceResponse.data.success) {
          setAttendanceChartData(attendanceResponse.data.data);
        } else {
          console.warn(
            "Erreur lors du chargement des données de présence:",
            attendanceResponse.message
          );
          setAttendanceChartData([]);
        }

        if (departmentResponse.data.success) {
          setDepartmentStats(departmentResponse.data.data);
        } else {
          console.warn(
            "Erreur lors du chargement des statistiques par département:",
            departmentResponse.message
          );
          setDepartmentStats([]);
        }
      } catch (error) {
        console.error("Erreur lors du chargement du tableau de bord:", error);
        setError("Erreur lors du chargement des données");
        toast({
          title: "Erreur",
          description: "Impossible de charger les données du tableau de bord",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [toast]);

  const handleQuickAction = (action) => {
    switch (action) {
      case "leave-request":
        navigate("/dashboard/leave-management");
        break;
      case "attendance":
        navigate("/dashboard/attendance");
        break;
      case "add-employee":
        navigate("/dashboard/employees");
        break;
      case "reports":
        navigate("/dashboard/payroll");
        break;
      case "approve-leaves":
        navigate("/dashboard/leave-management");
        break;
      case "team-attendance":
        navigate("/dashboard/attendance");
        break;
      case "training":
        navigate("/dashboard/training");
        break;
      case "expenses":
        navigate("/dashboard/payroll");
        break;
      case "recruitment":
        navigate("/dashboard/recruitment");
        break;
      case "compliance":
        navigate("/dashboard/employees");
        break;
      default:
        break;
    }
  };

  const handleStatCardClick = (type) => {
    switch (type) {
      case "employees":
        navigate("/dashboard/employees");
        break;
      case "leave":
        navigate("/dashboard/leave-management");
        break;
      case "departments":
        navigate("/dashboard/departments");
        break;
      case "recruitment":
        navigate("/dashboard/recruitment");
        break;
      case "attendance":
        navigate("/dashboard/attendance");
        break;
      case "payroll":
        navigate("/dashboard/payroll");
        break;
      case "training":
        navigate("/dashboard/training");
        break;
      default:
        break;
    }
  };

  // Prepare chart data
  const prepareAttendanceChartData = () => {
    if (!attendanceChartData) return [];
    return attendanceChartData.map((item) => ({
      label: item.month,
      value: item.attendance,
    }));
  };

  const prepareDepartmentChartData = () => {
    if (!departmentStats) return [];
    return departmentStats.map((item) => ({
      label: item.name,
      value: item.employees,
    }));
  };

  const prepareHealthcareChartData = () => {
    if (!healthcareMetrics) return [];

    // Ensure all values are positive and meaningful for visualization
    const documentsValid = Math.max(healthcareMetrics.complianceRate || 0, 1);
    const trainingComplete = Math.max(
      healthcareMetrics.trainingComplianceRate || 0,
      1
    );
    const satisfaction = Math.max(healthcareMetrics.satisfactionRate || 0, 1);

    return [
      { label: "Documents Valides", value: documentsValid },
      { label: "Formation Complète", value: trainingComplete },
      { label: "Satisfaction", value: satisfaction },
    ].filter((item) => item.value > 0); // Only show items with positive values
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Erreur de chargement
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
          <Button
            onClick={() => window.location.reload()}
            className="mt-4"
            variant="outline"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Section with Healthcare Theme */}
      <div className="bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                Bonjour, Dr. {user?.prenom} {user?.nom} !
              </h1>
              <p className="text-lg opacity-90 mb-4">
                Bienvenue sur votre tableau de bord RH. Surveillez la santé de
                votre organisation.
              </p>
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Heart className="h-4 w-4" />
                  <span>Santé de l'organisation</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="h-4 w-4" />
                  <span>Conformité médicale</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Award className="h-4 w-4" />
                  <span>Excellence des soins</span>
                </div>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="p-6 rounded-full bg-white/20 backdrop-blur-sm">
                <Activity className="h-12 w-12" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Dashboard Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:grid-cols-4">
          <TabsTrigger value="overview" className="flex items-center space-x-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Vue d'ensemble</span>
          </TabsTrigger>
          <TabsTrigger
            value="healthcare"
            className="flex items-center space-x-2"
          >
            <Heart className="h-4 w-4" />
            <span className="hidden sm:inline">Santé</span>
          </TabsTrigger>
          <TabsTrigger
            value="activities"
            className="flex items-center space-x-2"
          >
            <Activity className="h-4 w-4" />
            <span className="hidden sm:inline">Activités</span>
          </TabsTrigger>
          <TabsTrigger value="actions" className="flex items-center space-x-2">
            <Zap className="h-4 w-4" />
            <span className="hidden sm:inline">Actions</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Employés"
              value={dashboardData?.totalEmployees || 0}
              icon={Users}
              color="blue"
              trend={dashboardData?.trends?.employees}
              isLoading={isLoading}
              onClick={() => handleStatCardClick("employees")}
            />
            <StatCard
              title="Employés Actifs"
              value={dashboardData?.activeEmployees || 0}
              icon={UserCheck}
              color="green"
              trend={dashboardData?.trends?.employees}
              isLoading={isLoading}
              onClick={() => handleStatCardClick("employees")}
            />
            <StatCard
              title="En Congé"
              value={dashboardData?.onLeave || 0}
              icon={Calendar}
              color="yellow"
              trend={dashboardData?.trends?.onLeave}
              isLoading={isLoading}
              onClick={() => handleStatCardClick("leave")}
            />
            <StatCard
              title="Demandes en Attente"
              value={dashboardData?.pendingLeave || 0}
              icon={AlertCircle}
              color="red"
              trend={dashboardData?.trends?.pendingLeave}
              isLoading={isLoading}
              onClick={() => handleStatCardClick("leave")}
            />
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartAreaInteractive
              title="Tendance de Présence"
              description="Taux de présence mensuel"
              data={prepareAttendanceChartData()}
              type="line"
              color="#10b981"
              height={250}
              isLoading={isLoading}
            />
            <ChartAreaInteractive
              title="Répartition par Département"
              description="Nombre d'employés par département"
              data={prepareDepartmentChartData()}
              type="bar"
              color="#3b82f6"
              height={250}
              isLoading={isLoading}
            />
          </div>

          {/* Additional Metrics for Admin/HR */}
          {["admin", "rh"].includes(user?.role) && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Départements"
                value={dashboardData?.departments || 0}
                icon={Building2}
                color="indigo"
                isLoading={isLoading}
                onClick={() => handleStatCardClick("departments")}
              />
              <StatCard
                title="Nouvelles Embauches"
                value={dashboardData?.newHires || 0}
                icon={UserPlus}
                color="green"
                trend={dashboardData?.trends?.newHires}
                isLoading={isLoading}
                onClick={() => handleStatCardClick("recruitment")}
              />
              <StatCard
                title="Taux de Présence"
                value={
                  dashboardData?.attendanceRate
                    ? `${dashboardData.attendanceRate}%`
                    : "0%"
                }
                icon={Clock}
                color="teal"
                isLoading={isLoading}
                onClick={() => handleStatCardClick("attendance")}
              />
              <StatCard
                title="Taux de Rotation"
                value={
                  dashboardData?.turnoverRate !== undefined
                    ? `${dashboardData.turnoverRate}%`
                    : "0%"
                }
                icon={TrendingUp}
                color="purple"
                isLoading={isLoading}
              />
            </div>
          )}

          {/* Healthcare-Specific Metrics */}
          {healthcareMetrics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Conformité"
                value={`${healthcareMetrics.complianceRate}%`}
                icon={Shield}
                color="teal"
                subtitle="Documents valides"
                isLoading={isLoading}
              />
              <StatCard
                title="Formation"
                value={`${healthcareMetrics.trainingComplianceRate}%`}
                icon={GraduationCap}
                color="blue"
                subtitle="Compliance formation"
                isLoading={isLoading}
              />
              <StatCard
                title="Satisfaction"
                value={`${healthcareMetrics.satisfactionRate}%`}
                icon={Star}
                color="yellow"
                subtitle="Satisfaction équipe"
                isLoading={isLoading}
              />
              <StatCard
                title="Incidents Sécurité"
                value={healthcareMetrics.safetyIncidents || 0}
                icon={AlertTriangle}
                color="red"
                subtitle="Ce mois"
                isLoading={isLoading}
              />
            </div>
          )}

          {/* Real-time Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Présence Aujourd'hui"
              value={dashboardData?.todayAttendance || 0}
              icon={Clock3}
              color="green"
              subtitle="Employés présents"
              isLoading={isLoading}
              onClick={() => handleStatCardClick("attendance")}
            />
            <StatCard
              title="Notes de Frais"
              value={dashboardData?.pendingExpenses || 0}
              icon={CreditCard}
              color="orange"
              subtitle="En attente"
              isLoading={isLoading}
              onClick={() => handleQuickAction("expenses")}
            />
            <StatCard
              title="Formations à Venir"
              value={dashboardData?.upcomingTrainings || 0}
              icon={BookOpen}
              color="purple"
              subtitle="Sessions planifiées"
              isLoading={isLoading}
              onClick={() => handleQuickAction("training")}
            />
            <StatCard
              title="Entretiens"
              value={dashboardData?.pendingInterviews || 0}
              icon={Briefcase}
              color="indigo"
              subtitle="En attente"
              isLoading={isLoading}
              onClick={() => handleQuickAction("recruitment")}
            />
          </div>
        </TabsContent>

        {/* Healthcare Tab */}
        <TabsContent value="healthcare" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Compliance Progress */}
            <Card className="transition-all duration-300 hover:shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-teal-600" />
                  <span>Conformité Médicale</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ProgressCard
                  title="Documents Valides"
                  value={healthcareMetrics?.employeesWithValidDocuments || 0}
                  target={healthcareMetrics?.totalEmployees || 1}
                  icon={FileText}
                  color="teal"
                  isLoading={isLoading}
                />
                <ProgressCard
                  title="Formation Complète"
                  value={healthcareMetrics?.employeesWithTraining || 0}
                  target={healthcareMetrics?.totalEmployees || 1}
                  icon={GraduationCap}
                  color="blue"
                  isLoading={isLoading}
                />
              </CardContent>
            </Card>

            {/* Quality Metrics */}
            <Card className="transition-all duration-300 hover:shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Award className="h-5 w-5 text-yellow-600" />
                  <span>Métriques de Qualité</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Heart className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-900 dark:text-green-100">
                        Satisfaction Patient
                      </p>
                      <p className="text-sm text-green-600 dark:text-green-400">
                        Basé sur les retours
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      95%
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400">
                      +2.3%
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Target className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900 dark:text-blue-100">
                        Temps de Réponse
                      </p>
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        Moyenne en minutes
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                      3.2
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      -0.5 min
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Healthcare Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartAreaInteractive
              title="Métriques de Conformité"
              description="Répartition des indicateurs de santé"
              data={prepareHealthcareChartData()}
              type="pie"
              height={300}
              isLoading={isLoading}
            />
            <ChartAreaInteractive
              title="Évolution de la Conformité"
              description="Tendance sur 12 mois"
              data={[
                { label: "Jan", value: 85 },
                { label: "Fév", value: 87 },
                { label: "Mar", value: 89 },
                { label: "Avr", value: 91 },
                { label: "Mai", value: 93 },
                { label: "Jun", value: 95 },
                { label: "Jul", value: 94 },
                { label: "Aoû", value: 96 },
                { label: "Sep", value: 97 },
                { label: "Oct", value: 98 },
                { label: "Nov", value: 99 },
                { label: "Déc", value: 100 },
              ]}
              type="line"
              color="#06b6d4"
              height={300}
              isLoading={isLoading}
            />
          </div>
        </TabsContent>

        {/* Activities Tab */}
        <TabsContent value="activities" className="space-y-6">
          <Card className="transition-all duration-300 hover:shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Activity className="h-5 w-5" />
                <span>Activité Récente</span>
              </CardTitle>
              <CardDescription>
                Dernières activités dans votre organisation
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="space-y-4 p-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <ActivityItem key={i} isLoading />
                  ))}
                </div>
              ) : recentActivities && recentActivities.length > 0 ? (
                <div className="space-y-1">
                  {recentActivities.slice(0, 8).map((activity, index) => (
                    <ActivityItem
                      key={activity.id || index}
                      title={activity.title}
                      description={activity.description}
                      subtitle={activity.subtitle}
                      time={activity.timestamp}
                      type={activity.type}
                      user={
                        activity.type === "new_employee"
                          ? activity.description
                          : undefined
                      }
                      status={activity.status}
                    />
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune activité récente</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quick Actions Tab */}
        <TabsContent value="actions" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Employee Actions */}
            {user?.role === "employe" && (
              <>
                <QuickActionButton
                  icon={Calendar}
                  title="Demander un Congé"
                  subtitle="Soumettre une demande de congé"
                  onClick={() => handleQuickAction("leave-request")}
                  variant="primary"
                />
                <QuickActionButton
                  icon={Clock}
                  title="Pointer"
                  subtitle="Enregistrer entrée/sortie"
                  onClick={() => handleQuickAction("attendance")}
                  variant="secondary"
                />
                <QuickActionButton
                  icon={CreditCard}
                  title="Note de Frais"
                  subtitle="Soumettre une note de frais"
                  onClick={() => handleQuickAction("expenses")}
                  variant="success"
                />
                <QuickActionButton
                  icon={BookOpen}
                  title="Mes Formations"
                  subtitle="Voir mes formations"
                  onClick={() => handleQuickAction("training")}
                  variant="healthcare"
                />
              </>
            )}

            {/* Manager Actions */}
            {user?.role === "manager" && (
              <>
                <QuickActionButton
                  icon={CheckCircle}
                  title="Approuver Congés"
                  subtitle="Gérer les demandes d'équipe"
                  onClick={() => handleQuickAction("approve-leaves")}
                  variant="warning"
                  badge={dashboardData?.pendingLeave || 0}
                />
                <QuickActionButton
                  icon={Users}
                  title="Présence Équipe"
                  subtitle="Voir les présences"
                  onClick={() => handleQuickAction("team-attendance")}
                  variant="secondary"
                />
                <QuickActionButton
                  icon={CreditCard}
                  title="Notes de Frais"
                  subtitle="Approuver les frais"
                  onClick={() => handleQuickAction("expenses")}
                  variant="success"
                  badge={dashboardData?.pendingExpenses || 0}
                />
                <QuickActionButton
                  icon={BookOpen}
                  title="Formations Équipe"
                  subtitle="Gérer les formations"
                  onClick={() => handleQuickAction("training")}
                  variant="healthcare"
                />
              </>
            )}

            {/* HR/Admin Actions */}
            {["admin", "rh"].includes(user?.role) && (
              <>
                <QuickActionButton
                  icon={UserPlus}
                  title="Ajouter Employé"
                  subtitle="Créer un nouveau profil"
                  onClick={() => handleQuickAction("add-employee")}
                  variant="success"
                />
                <QuickActionButton
                  icon={UserPlus}
                  title="Recrutement"
                  subtitle="Gérer les candidatures"
                  onClick={() => handleQuickAction("recruitment")}
                  variant="primary"
                  badge={dashboardData?.pendingInterviews || 0}
                />
                <QuickActionButton
                  icon={TrendingUp}
                  title="Rapports"
                  subtitle="Générer des rapports"
                  onClick={() => handleQuickAction("reports")}
                  variant="secondary"
                />
                <QuickActionButton
                  icon={Shield}
                  title="Conformité"
                  subtitle="Vérifier la conformité"
                  onClick={() => handleQuickAction("compliance")}
                  variant="healthcare"
                />
                <QuickActionButton
                  icon={BookOpen}
                  title="Formations"
                  subtitle="Gérer les formations"
                  onClick={() => handleQuickAction("training")}
                  variant="purple"
                  badge={dashboardData?.upcomingTrainings || 0}
                />
                <QuickActionButton
                  icon={CreditCard}
                  title="Notes de Frais"
                  subtitle="Approuver les frais"
                  onClick={() => handleQuickAction("expenses")}
                  variant="warning"
                  badge={dashboardData?.pendingExpenses || 0}
                />
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
